import React from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { zodResolver } from '@hookform/resolvers/zod';
import { AxiosError } from 'axios';
import { useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import SquareIcon from 'src/registry/components/blocks/SquareIcon';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { SmartParkError } from 'src/registry/types/axios';
import { Button } from 'src/registry/ui/Button';
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogSeparator,
    DialogTitle,
    DialogTrigger,
} from 'src/registry/ui/Dialog';
import { generalMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';
import { useOnError } from 'src/utils/errors';
import { ActiveStay } from 'src/features/stay/types';
import { exitFromParkingFormSchema, ExitFromParkingFormSchemaValues } from 'src/features/stay/utils/formSchema';
import { visitorMessages } from 'src/features/visitor/utils/messages';
import ExitFromParkingForm from 'src/features/stay/components/forms/ExitFromParkingForm';

interface ExitFromParkingModalProps<TData> extends DataTableNewEntryProps<TData> {
    activeStay: ActiveStay;
    children?: React.ReactNode;
    isOpen?: boolean;
    isPending: boolean;
    mutate: (
        data: ExitFromParkingFormSchemaValues,
        onSuccess: () => void,
        onError: (error: AxiosError<SmartParkError, any>) => void,
    ) => void;
}

function ExitFromParkingModal<TData>({ activeStay, children, mutate, isPending }: ExitFromParkingModalProps<TData>) {
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const { formatMessage } = useIntl();

    const defaultValues = {
        exit_gateway: undefined,
        open_gateway: undefined,
    };

    const form = useForm<ExitFromParkingFormSchemaValues>({
        resolver: zodResolver(exitFromParkingFormSchema),
        defaultValues,
    });

    const onOpenChange = (isOpen: boolean) => {
        if (!isOpen) form.reset();
        setIsModalOpen(isOpen);
    };

    const onError = useOnError();

    const onSubmit = React.useCallback(
        (values: ExitFromParkingFormSchemaValues) => {
            form.reset();
            mutate(
                values,
                () => setIsModalOpen(false),
                (error) => onError(error, form.setError),
            );
        },
        [form, mutate, onError],
    );

    return (
        <Dialog modal open={isModalOpen} onOpenChange={onOpenChange}>
            {children && <DialogTrigger asChild>{children}</DialogTrigger>}
            <DialogContent>
                <DialogHeader className="flex-row gap-2">
                    <SquareIcon icon={ICON_MAP.ARROW_LEFT_FROM_ARC} />
                    <div>
                        <DialogTitle>{formatMessage(visitorMessages.exitFromParking)}</DialogTitle>
                        <DialogDescription>
                            {formatMessage(visitorMessages.exitFromParkingSubtitle, {
                                client: activeStay.main_identifier.value,
                            })}
                        </DialogDescription>
                    </div>
                </DialogHeader>
                <DialogSeparator />
                <DialogBody>
                    <ExitFromParkingForm form={form} onSubmit={onSubmit} />
                </DialogBody>
                <DialogSeparator />
                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="outline" type="button" className="flex-1">
                            <FormattedMessage {...generalMessages.cancel} />
                        </Button>
                    </DialogClose>
                    <Button
                        variant="default"
                        disabled={isPending}
                        type="submit"
                        onClick={form.handleSubmit(onSubmit)}
                        className="flex-1"
                    >
                        {isPending && <FontAwesomeIcon icon={ICON_MAP.SPINNER} className="mr-2 size-4 animate-spin" />}
                        {formatMessage(visitorMessages.exit)}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default ExitFromParkingModal;
